/**
 * Sort utilities for RCA measurement section lists (Key Sensors, Drift Summary,
 * Remaining Sensors). Callers pass filtered entries from `rankKeyTrends`,
 * `rankRemainingTrends`, or `buildDriftEntries`; this module owns all display
 * ordering for both score and alphabetical modes.
 *
 * @module rca-measurement-sort
 */

/**
 * How a section list should be ordered for display.
 *
 * - `score` — Key/remaining: `score.total`. Drift: |drift|.
 * - `alphabetical` — Tag description (case-insensitive), then `AssetTagId`.
 *
 * @typedef {'score' | 'alphabetical'} RcaMeasurementSortMode
 */

/**
 * @typedef {'asc' | 'desc'} RcaMeasurementSortDirection
 */

/**
 * Sort options shared by key-trend and drift measurement section lists.
 *
 * @typedef {Object} RcaMeasurementSortOptions
 * @property {RcaMeasurementSortMode} sortMode
 * @property {RcaMeasurementSortDirection} [sortDirection='desc']
 */

/**
 * Minimum measurement-row fields used for alphabetical ordering.
 * Rows without `TagDescription` fall back to ``Tag ${AssetTagId}``.
 *
 * @typedef {Object} RcaMeasurementRowLabelFields
 * @property {number} [AssetTagId] — Tiebreaker when display labels match.
 * @property {string} [TagDescription] — Primary alphabetical sort key.
 */

/**
 * Score payload on key/remaining trend entries. Only `total` is read for sorting.
 * Aligns with `KeyTrendScoreBreakdown` from `rca-key-trend-score.js`.
 *
 * @typedef {Object} RcaSortableTrendScore
 * @property {number} total — Weighted key-trend points (0–100).
 */

/**
 * One key-trend row with a precomputed score, as returned by `rankKeyTrends` or
 * `rankRemainingTrends` (unordered until passed to `sortKeyedTrendEntries`).
 *
 * @typedef {Object} RcaKeyedTrendEntry
 * @property {RcaMeasurementRowLabelFields & Record<string, unknown>} row
 * @property {RcaSortableTrendScore} score
 */

/**
 * One qualifying drift row from `buildDriftEntries` in
 * `pre-shutdown-drift-summary.jsx`. Extra fields are preserved but ignored for sorting.
 *
 * @typedef {Object} RcaDriftEntry
 * @property {RcaMeasurementRowLabelFields & Record<string, unknown>} row
 * @property {number} drift — Normalized (mean_last − mean_first) / range.
 */

/**
 * @param {number} a
 * @param {number} b
 * @param {RcaMeasurementSortDirection} direction
 */
function compareNumbers(a, b, direction) {
  const diff = a - b;
  return direction === "asc" ? diff : -diff;
}

/**
 * Comparator for alphabetical ordering of measurement rows by display label.
 *
 * @param {RcaMeasurementRowLabelFields} a
 * @param {RcaMeasurementRowLabelFields} b
 * @param {RcaMeasurementSortDirection} [direction='asc']
 * @returns {number} Standard sort comparator: negative if `a` before `b`, positive if after, 0 if equal.
 */
function compareMeasurementRowsByLabel(a, b, direction = "asc") {
  const da = String(a.TagDescription ?? "").trim() || `Tag ${a.AssetTagId}`;
  const db = String(b.TagDescription ?? "").trim() || `Tag ${b.AssetTagId}`;
  const cmp = da.localeCompare(db, undefined, { sensitivity: "base" });
  const labelCmp =
    cmp !== 0 ? cmp : Number(a.AssetTagId) - Number(b.AssetTagId);
  return direction === "asc" ? labelCmp : -labelCmp;
}

/**
 * @param {RcaKeyedTrendEntry} a
 * @param {RcaKeyedTrendEntry} b
 * @param {RcaMeasurementSortDirection} direction
 */
function compareKeyedTrendEntriesByScore(a, b, direction) {
  const scoreDiff = compareNumbers(a.score.total, b.score.total, direction);
  if (scoreDiff !== 0) return scoreDiff;
  return compareMeasurementRowsByLabel(a.row, b.row, "asc");
}

/**
 * @param {RcaDriftEntry} a
 * @param {RcaDriftEntry} b
 */
function driftSortMagnitude(a) {
  return Number.isFinite(a.drift) ? Math.abs(a.drift) : null;
}

/**
 * @param {RcaDriftEntry} a
 * @param {RcaDriftEntry} b
 * @param {RcaMeasurementSortDirection} direction
 */
function compareDriftEntriesByScore(a, b, direction) {
  const va = driftSortMagnitude(a);
  const vb = driftSortMagnitude(b);
  if (va === null && vb === null) return 0;
  if (va === null) return 1;
  if (vb === null) return -1;
  return compareNumbers(va, vb, direction);
}

/**
 * Sorts key-trend entries for display. Handles both score and alphabetical
 * modes plus ascending or descending order.
 *
 * @param {RcaKeyedTrendEntry[]} entries — From `rankKeyTrends` or `rankRemainingTrends`.
 * @param {RcaMeasurementSortOptions} options
 * @returns {RcaKeyedTrendEntry[]}
 */
export function sortKeyedTrendEntries(entries, options) {
  const { sortMode, sortDirection = "desc" } = options;

  const comparator =
    sortMode === "alphabetical"
      ? (a, b) => compareMeasurementRowsByLabel(a.row, b.row, sortDirection)
      : (a, b) => compareKeyedTrendEntriesByScore(a, b, sortDirection);

  return [...entries].sort(comparator);
}

/**
 * Sorts drift summary entries for display. Score mode uses |drift|; non-finite
 * values are placed last regardless of direction.
 *
 * @param {RcaDriftEntry[]} entries — From `buildDriftEntries`.
 * @param {RcaMeasurementSortOptions} options
 * @returns {RcaDriftEntry[]}
 */
export function sortDriftEntries(entries, options) {
  const { sortMode, sortDirection = "desc" } = options;

  const comparator =
    sortMode === "alphabetical"
      ? (a, b) => compareMeasurementRowsByLabel(a.row, b.row, sortDirection)
      : (a, b) => compareDriftEntriesByScore(a, b, sortDirection);

  return [...entries].sort(comparator);
}
